/* Find Parking — list + map screen. Values follow the AirGarage design system (sand neutrals, orange-9 accent). */

.page {
    display: flex;
    flex-direction: column;
    /* Exactly the viewport (body scrolling is disabled on this screen); the list and map scroll inside. */
    height: calc(100vh - var(--env-banner-height, 0px));
    height: calc(100dvh - var(--env-banner-height, 0px));
    background: #f9f9f8;
    color: #21201c;
    font-family: 'Outfit', sans-serif;
    -webkit-font-smoothing: antialiased;
}

/* The map fills the whole area; the results panel floats over it as frosted glass. */
.grid {
    --sidebar-width: clamp(360px, 32vw, 480px);
    --panel-inset: 16px;
    position: relative;
    flex: 1;
    min-height: 0;
}

/* ── Sidebar ─────────────────────────────────────────────────── */

.sidebar {
    position: absolute;
    top: var(--panel-inset);
    left: var(--panel-inset);
    bottom: var(--panel-inset);
    width: var(--sidebar-width);
    z-index: 3;
    display: flex;
    flex-direction: column;
    min-height: 0;
    border: 1px solid rgba(255, 255, 255, 0.65);
    border-radius: 16px;
    background: rgba(249, 249, 248, 0.72);
    -webkit-backdrop-filter: blur(18px) saturate(1.3);
    backdrop-filter: blur(18px) saturate(1.3);
    box-shadow: 0 8px 24px rgba(21, 23, 26, 0.08), 0 2px 4px rgba(132, 142, 151, 0.07);
    overflow: hidden;
}

.sidebarHeader {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 16px 24px;
    position: relative;
    z-index: 2;
    border-bottom: 1px solid rgba(226, 225, 222, 0.8);
}

.pageLabel {
    font-size: 14px;
    color: #63635e;
}

.list {
    display: flex;
    flex-direction: column;
    gap: 12px;
    padding: 16px 24px 24px;
    overflow-y: auto;
    min-height: 0;
}

.list::-webkit-scrollbar {
    width: 8px;
}

.list::-webkit-scrollbar-thumb {
    background: #dad9d6;
    border-radius: 9999px;
}

.emptyState {
    padding: 24px;
    text-align: center;
    font-size: 14px;
    line-height: 20px;
    color: #63635e;
}

/* ── Location card ───────────────────────────────────────────── */

.card {
    border: 1px solid #e2e1de;
    border-radius: 12px;
    background: #ffffff;
    padding: 20px;
    box-shadow: none;
    cursor: pointer;
    transition: border-color 150ms ease, box-shadow 150ms ease;
}

.cardActive {
    border-color: #ffa581;
    box-shadow: 0 4px 16px rgba(132, 142, 151, 0.14);
}

/* Pin selected on the map: solid accent border plus the design system's soft accent ring. */
.cardSelected,
.cardSelected.cardActive {
    border-color: #ff6712;
    background: #fefcfb;
    box-shadow: 0 0 0 4px #ffe9dd, 0 4px 16px rgba(132, 142, 151, 0.14);
}

.cardTop {
    display: flex;
    gap: 14px;
    align-items: flex-start;
}

.photo,
.photoPlaceholder {
    width: 96px;
    height: 96px;
    flex-shrink: 0;
    border-radius: 8px;
    background: #f1f0ef;
}

.photo {
    object-fit: cover;
    display: block;
}

.photoPlaceholder {
    display: flex;
    align-items: center;
    justify-content: center;
    color: #8d8d86;
}

.cardBody {
    flex: 1;
    min-width: 0;
}

.name {
    margin: 0;
    font-size: 18px;
    font-weight: 600;
    line-height: 24px;
}

.address {
    font-size: 14px;
    color: #63635e;
    line-height: 20px;
    margin-top: 2px;
}

.metaRow {
    display: flex;
    gap: 12px;
    margin-top: 8px;
    font-size: 13px;
    color: #63635e;
    flex-wrap: wrap;
}

.metaItem {
    display: flex;
    align-items: center;
    gap: 4px;
}

.tabs {
    display: flex;
    gap: 20px;
    margin-top: 16px;
    border-bottom: 1px solid #e2e1de;
}

.tab {
    display: flex;
    align-items: center;
    gap: 6px;
    height: 36px;
    padding: 0;
    margin-bottom: -1px;
    border: 0;
    border-bottom: 2px solid transparent;
    background: transparent;
    color: #63635e;
    font-family: inherit;
    font-size: 14px;
    font-weight: 500;
    cursor: pointer;
    transition: color 150ms ease, border-color 150ms ease;
}

.tab:hover {
    color: #21201c;
}

.tabActive,
.tabActive:hover {
    color: #21201c;
    border-bottom-color: #ff6712;
    font-weight: 600;
}

.priceRow {
    display: flex;
    justify-content: space-between;
    align-items: baseline;
    gap: 12px;
    margin-top: 14px;
    flex-wrap: wrap;
}

.priceGroup {
    display: flex;
    align-items: baseline;
    gap: 6px;
}

.price {
    font-size: 26px;
    font-weight: 600;
    line-height: 32px;
}

.priceUnit,
.priceNote {
    color: #63635e;
}

.priceUnit {
    font-size: 14px;
}

.priceNote {
    font-size: 13px;
}

/* Moving-rate schedule ("You stay / You pay"), mirroring QuickPay's rates table. */
.tierTable {
    margin-top: 12px;
    padding: 10px 12px;
    border: 1px solid #e2e1de;
    border-radius: 8px;
    font-size: 13px;
    line-height: 20px;
}

.tierHeader {
    display: flex;
    justify-content: space-between;
    margin-bottom: 4px;
    font-size: 12px;
    line-height: 16px;
    color: #63635e;
}

.tierRow {
    display: flex;
    justify-content: space-between;
    gap: 12px;
}

.tierPrice {
    font-weight: 600;
    white-space: nowrap;
}

.rateTerms {
    margin: 8px 0 0;
    font-size: 13px;
    line-height: 18px;
    color: #63635e;
}

/* Individual vs Business Account choice on the Monthly tab. */
.accountPicker {
    display: grid;
    grid-template-columns: repeat(2, minmax(0, 1fr));
    gap: 8px;
    margin-top: 12px;
}

.accountOption {
    display: flex;
    flex-direction: column;
    gap: 2px;
    padding: 10px 12px;
    border: 1px solid #e2e1de;
    border-radius: 8px;
    background: #ffffff;
    color: #21201c;
    font-family: inherit;
    text-align: left;
    cursor: pointer;
    transition: border-color 150ms ease, box-shadow 150ms ease, background 150ms ease;
}

.accountOption:hover {
    border-color: #cfceca;
}

.accountOptionActive,
.accountOptionActive:hover {
    border-color: #ff6712;
    background: #fff5f0;
    box-shadow: 0 0 0 3px #ffe9dd;
}

.accountOptionTitle {
    display: flex;
    justify-content: space-between;
    align-items: center;
    gap: 8px;
    font-size: 13px;
    font-weight: 600;
    line-height: 18px;
}

.accountOptionText {
    font-size: 12px;
    line-height: 16px;
    color: #63635e;
}

/* Business Account uses the design system's blue so the booking reads as distinct. */
.accountOptionActiveBusiness,
.accountOptionActiveBusiness:hover {
    border-color: #0090ff;
    background: #f4faff;
    box-shadow: 0 0 0 3px #e6f4fe;
}

.accountRadioOnBusiness {
    flex-shrink: 0;
    color: #0090ff;
}

.primaryButton.primaryButtonBusiness {
    background: #0090ff;
}

.primaryButton.primaryButtonBusiness:hover {
    background: #0588f0;
}

.accountRadio {
    flex-shrink: 0;
    color: #bcbbb5;
}

.accountRadioOn {
    flex-shrink: 0;
    color: #ff6712;
}

@media (max-width: 480px) {
    .accountPicker {
        grid-template-columns: minmax(0, 1fr);
    }
}

.notice {
    display: flex;
    gap: 10px;
    align-items: flex-start;
    margin-top: 12px;
    padding: 10px 12px;
    background: #f1f0ef;
    border-radius: 8px;
    font-size: 13px;
    line-height: 18px;
    color: #63635e;
}

.noticeIcon {
    flex-shrink: 0;
    margin-top: 1px;
}

.actions {
    display: flex;
    justify-content: space-between;
    align-items: center;
    gap: 12px;
    margin-top: 16px;
}

.neutralButton,
.primaryButton {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    height: 40px;
    border-radius: 6px;
    border: 0;
    font-family: inherit;
    font-size: 14px;
    text-decoration: none;
    cursor: pointer;
    transition: background 150ms ease;
}

.neutralButton {
    padding: 0 16px;
    box-shadow: inset 0 0 0 1px #e2e1de;
    background: #ffffff;
    color: #21201c;
    font-weight: 500;
}

.neutralButton:hover {
    background: #f1f0ef;
    color: #21201c;
}

.primaryButton {
    flex: 1;
    gap: 8px;
    padding: 0 18px;
    background: #ff6712;
    color: #ffffff;
    font-weight: 600;
}

.primaryButton:hover {
    background: #f35900;
    color: #ffffff;
}

/* ── Map ─────────────────────────────────────────────────────── */

.mapPane {
    position: absolute;
    top: 0;
    right: 0;
    bottom: 0;
    left: 0;
    min-height: 0;
    overflow: hidden;
    background: #e6eef3;
}

/* mapbox-gl.css sets Helvetica on the map element; pins and popups render inside it. */
.mapPane :global(.mapboxgl-map) {
    font-family: 'Outfit', sans-serif;
}

.mapControl {
    position: absolute;
    z-index: 5;
    background: #ffffff;
    border-radius: 8px;
    box-shadow: 0 2px 8px rgba(132, 142, 151, 0.14);
}

.mapTopLeft {
    position: absolute;
    top: 16px;
    left: calc(var(--sidebar-width) + var(--panel-inset) * 2);
    right: 72px;
    z-index: 5;
    display: flex;
    flex-wrap: wrap;
    gap: 8px;
}

.togglePill {
    display: flex;
    align-items: center;
    gap: 10px;
    height: 40px;
    padding: 0 14px;
    border: 0;
    border-radius: 8px;
    background: #ffffff;
    box-shadow: 0 2px 8px rgba(132, 142, 151, 0.14);
    color: #21201c;
    font-family: inherit;
    font-size: 14px;
    cursor: pointer;
    user-select: none;
    white-space: nowrap;
}

/* Floating "Search Here": shown only after the map moves away from the last search. Fades and slides in. */
.searchHere {
    position: absolute;
    bottom: 28px;
    /* Centered over the visible map, to the right of the panel. */
    left: calc(50% + var(--sidebar-width) / 2);
    z-index: 5;
    display: inline-flex;
    align-items: center;
    gap: 6px;
    padding: 8px 16px;
    border: 1px solid #e2e1de;
    border-radius: 9999px;
    background: #ffffff;
    color: #21201c;
    box-shadow: 0 6px 16px rgba(0, 0, 0, 0.14);
    font-family: inherit;
    font-size: 13px;
    font-weight: 600;
    cursor: pointer;
    opacity: 0;
    visibility: hidden;
    pointer-events: none;
    transform: translateX(-50%) translateY(8px);
    transition: opacity 180ms ease, transform 220ms cubic-bezier(0.16, 1, 0.3, 1), background 150ms ease;
}

.searchHereVisible {
    opacity: 1;
    visibility: visible;
    pointer-events: auto;
    transform: translateX(-50%) translateY(0);
}

.searchHere:hover {
    background: #f9f9f8;
}

.searchHereIcon {
    color: #d74600;
}

.poiTooltip {
    display: flex;
    flex-direction: column;
    padding: 6px 10px;
    background: #ffffff;
    border-radius: 8px;
    box-shadow: 0 8px 24px rgba(132, 142, 151, 0.22);
    white-space: nowrap;
}

.poiTooltipName {
    font-size: 13px;
    font-weight: 600;
    line-height: 18px;
}

.poiTooltipCategory {
    font-size: 12px;
    line-height: 16px;
    color: #63635e;
}

.checkbox {
    width: 18px;
    height: 18px;
    border-radius: 4px;
    border: 1px solid #cfceca;
    background: #ffffff;
    display: flex;
    align-items: center;
    justify-content: center;
    color: #ffffff;
    transition: background 150ms ease, border-color 150ms ease;
}

.checkboxOn {
    border-color: #ff6712;
    background: #ff6712;
}

.zoomStack {
    top: 16px;
    right: 16px;
    display: flex;
    flex-direction: column;
    overflow: hidden;
}

.zoomDivider {
    height: 1px;
    background: #e2e1de;
}

.mapButton {
    width: 40px;
    height: 40px;
    border: 0;
    background: #ffffff;
    color: #21201c;
    cursor: pointer;
    display: flex;
    align-items: center;
    justify-content: center;
    transition: background 150ms ease;
}

.mapButton:hover {
    background: #f1f0ef;
}

.locateButton {
    right: 16px;
    bottom: 16px;
    border-radius: 8px;
    overflow: hidden;
}

.layers {
    position: absolute;
    left: calc(var(--sidebar-width) + var(--panel-inset) * 2);
    bottom: 16px;
    z-index: 6;
    display: flex;
    flex-direction: column;
    align-items: flex-start;
    gap: 8px;
}

.layersButton {
    position: static;
    border-radius: 8px;
    overflow: hidden;
}

.layersPanel {
    display: grid;
    grid-template-columns: repeat(2, 1fr);
    gap: 8px;
    padding: 8px;
    background: #ffffff;
    border: 1px solid #e2e1de;
    border-radius: 12px;
    box-shadow: 0 8px 24px rgba(21, 23, 26, 0.08), 0 2px 4px rgba(132, 142, 151, 0.07);
}

.layerOption {
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 6px;
    padding: 6px;
    border: 1px solid transparent;
    border-radius: 10px;
    background: transparent;
    color: #63635e;
    font-family: inherit;
    font-size: 12px;
    font-weight: 500;
    cursor: pointer;
    transition: background 150ms ease, border-color 150ms ease, color 150ms ease;
}

.layerOption:hover {
    background: #f1f0ef;
    color: #21201c;
}

.layerOptionActive,
.layerOptionActive:hover {
    border-color: #ff6712;
    background: #fff5f0;
    color: #21201c;
    font-weight: 600;
}

.layerPreview {
    display: block;
    width: 72px;
    height: 72px;
    border-radius: 8px;
    border: 1px solid #e2e1de;
    background: #f1f0ef;
    object-fit: cover;
}

.layerLabel {
    line-height: 16px;
}

.loadingPill {
    top: 16px;
    left: 50%;
    transform: translateX(-50%);
    height: 40px;
    padding: 0 14px;
    display: flex;
    align-items: center;
    font-size: 14px;
    color: #63635e;
}

/* ── Pins + popup ────────────────────────────────────────────── */

.pin {
    position: relative;
    display: flex;
    flex-direction: column;
    align-items: center;
    cursor: pointer;
}

.pinDot {
    width: 24px;
    height: 24px;
    padding: 0;
    cursor: pointer;
    border-radius: 9999px;
    background: #ff6712;
    border: 3px solid #ffffff;
    box-shadow: 0 2px 6px rgba(89, 44, 25, 0.3);
    transition: width 150ms ease, height 150ms ease;
}

.pinDotActive {
    width: 32px;
    height: 32px;
}

/* Dark pin at the exact searched point, with a small chip underneath. */
.searchPin {
    position: relative;
    display: flex;
    justify-content: center;
    color: #21201c;
    filter: drop-shadow(0 2px 4px rgba(0, 0, 0, 0.3));
}

.searchPinLabel {
    position: absolute;
    top: 100%;
    margin-top: 2px;
    padding: 2px 8px;
    border-radius: 9999px;
    background: #ffffff;
    color: #21201c;
    font-size: 12px;
    font-weight: 600;
    line-height: 16px;
    white-space: nowrap;
}

/* Blue dot for the user's own location. */
.userPin {
    width: 18px;
    height: 18px;
    border-radius: 9999px;
    background: #0090ff;
    border: 3px solid #ffffff;
    box-shadow: 0 0 0 6px rgba(0, 144, 255, 0.2), 0 2px 6px rgba(0, 0, 0, 0.25);
}

.popup {
    position: absolute;
    bottom: calc(100% + 10px);
    left: 50%;
    transform: translateX(-50%);
    background: #ffffff;
    border-radius: 12px;
    box-shadow: 0 8px 24px rgba(132, 142, 151, 0.22);
    padding: 12px 14px;
    min-width: 220px;
    display: flex;
    flex-direction: column;
    gap: 8px;
    cursor: default;
    text-align: left;
}

.popupArrow {
    position: absolute;
    left: 50%;
    bottom: -6px;
    width: 12px;
    height: 12px;
    background: #ffffff;
    transform: translateX(-50%) rotate(45deg);
}

.popupName {
    font-size: 15px;
    font-weight: 600;
    line-height: 20px;
    white-space: nowrap;
}

.popupAddress {
    font-size: 12px;
    color: #63635e;
    line-height: 16px;
    margin-top: 2px;
}

.popupMeta {
    display: flex;
    align-items: center;
    gap: 4px;
    font-size: 12px;
    color: #63635e;
    line-height: 16px;
    margin-top: 4px;
}

.popupRates {
    display: flex;
    flex-direction: column;
    gap: 4px;
    border-top: 1px solid #e2e1de;
    padding-top: 8px;
}

.popupRate {
    display: flex;
    justify-content: space-between;
    gap: 12px;
    font-size: 13px;
    line-height: 18px;
}

.popupRateLabel {
    color: #63635e;
}

.popupRatePrice {
    font-weight: 600;
}

.popupRateUnit {
    font-weight: 400;
    color: #63635e;
}

.popupButton {
    display: flex;
    align-items: center;
    justify-content: center;
    height: 34px;
    border: 0;
    border-radius: 6px;
    background: #ff6712;
    color: #ffffff;
    font-size: 13px;
    font-weight: 600;
    text-decoration: none;
    cursor: pointer;
    transition: background 150ms ease;
}

.popupButton:hover {
    background: #f35900;
    color: #ffffff;
}

/* List / Map switch, shown only on narrow screens. */
.mobileViewToggle {
    display: none;
}

/* ── Narrow screens: one pane at a time, switched by the List / Map pill ── */

@media (max-width: 900px) {
    .grid {
        display: flex;
        flex-direction: column;
    }

    .mapPane {
        position: relative;
        flex: 1;
    }

    .gridList .mapPane,
    .gridMap .sidebar {
        display: none;
    }

    .sidebar {
        position: static;
        width: auto;
        flex: 1;
        border: 0;
        border-top: 1px solid #e2e1de;
        border-radius: 0;
        background: #f1f0ef;
        -webkit-backdrop-filter: none;
        backdrop-filter: none;
        box-shadow: none;
    }

    .mapTopLeft,
    .layers {
        left: 16px;
    }

    .searchHere {
        left: 50%;
        bottom: 76px;
    }

    .mobileViewToggle {
        position: absolute;
        bottom: 16px;
        left: 50%;
        transform: translateX(-50%);
        z-index: 7;
        display: flex;
        gap: 4px;
        padding: 4px;
        background: #ffffff;
        border: 1px solid #e2e1de;
        border-radius: 9999px;
        box-shadow: 0 6px 16px rgba(0, 0, 0, 0.14);
    }

    .mobileViewButton {
        display: flex;
        align-items: center;
        gap: 6px;
        height: 36px;
        padding: 0 16px;
        border: 0;
        border-radius: 9999px;
        background: transparent;
        color: #21201c;
        font-family: inherit;
        font-size: 14px;
        font-weight: 600;
        cursor: pointer;
        transition: background 150ms ease, color 150ms ease;
    }

    .mobileViewButtonActive {
        background: #ff6712;
        color: #ffffff;
    }

    .list {
        padding-bottom: 80px;
    }

    .sidebarHeader,
    .list {
        padding-left: 16px;
        padding-right: 16px;
    }
}

/* ── Search header ───────────────────────────────────────────── */

.searchHeader {
    display: flex;
    align-items: center;
    flex-wrap: wrap;
    gap: 12px 32px;
    padding: 16px 24px;
    background: #f9f9f8;
    border-bottom: 1px solid #e2e1de;
}

.title {
    margin: 0;
    font-family: inherit;
    font-size: 28px;
    font-weight: 600;
    line-height: 36px;
    white-space: nowrap;
}

.searchForm {
    position: relative;
    flex: 1 1 320px;
    max-width: 640px;
}

.searchField {
    position: relative;
    display: flex;
    align-items: center;
    gap: 10px;
    height: 56px;
    padding: 0 8px 0 16px;
    background: #ffffff;
    border: 1px solid #e2e1de;
    border-radius: 12px;
    transition: border-color 150ms ease, box-shadow 150ms ease;
}

.searchField:focus-within {
    border-color: #ffa581;
    box-shadow: 0 0 0 5px #ffe9dd;
}

.searchIcon,
.suggestionIcon {
    color: #8d8d86;
    flex-shrink: 0;
}

.searchInput {
    flex: 1;
    min-width: 0;
    height: 100%;
    padding: 0;
    border: 0;
    outline: none;
    background: transparent;
    color: #21201c;
    font-family: inherit;
    font-size: 16px;
}

.searchInput::placeholder {
    color: #8d8d86;
}

.searchButton {
    flex-shrink: 0;
    height: 40px;
    padding: 0 20px;
    border: 0;
    border-radius: 6px;
    background: #ff6712;
    color: #ffffff;
    font-family: inherit;
    font-size: 14px;
    font-weight: 600;
    cursor: pointer;
    transition: background 150ms ease;
}

.searchButton:hover {
    background: #f35900;
}

.searchButton:disabled {
    background: #ffa581;
    cursor: default;
}

.suggestions {
    position: absolute;
    top: calc(100% + 6px);
    left: 0;
    right: 0;
    z-index: 20;
    margin: 0;
    padding: 6px;
    list-style: none;
    background: #ffffff;
    border: 1px solid #e2e1de;
    border-radius: 12px;
    box-shadow: 0 8px 24px rgba(21, 23, 26, 0.08), 0 2px 4px rgba(132, 142, 151, 0.07);
}

.suggestion {
    display: flex;
    align-items: center;
    gap: 10px;
    padding: 10px 12px;
    border-radius: 8px;
    cursor: pointer;
    font-size: 14px;
    line-height: 20px;
}

.suggestionActive {
    background: #f1f0ef;
}

.suggestionText {
    display: flex;
    flex-direction: column;
    min-width: 0;
}

.suggestionName {
    font-weight: 500;
}

.suggestionDetail {
    font-size: 13px;
    color: #63635e;
}

.searchError {
    margin: 8px 0 0;
    font-size: 13px;
    line-height: 18px;
    color: #ce2c31;
}

@media (max-width: 900px) {
    .searchHeader {
        padding: 16px;
    }

    .title {
        font-size: 24px;
        line-height: 30px;
    }

    .searchForm {
        flex-basis: 100%;
        max-width: none;
    }

    .searchField {
        height: 52px;
    }
}
